import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type DomainStatus = 'active' | 'pending' | 'expired' | 'failed';
export type ManagedBy = 'Hospitable' | 'External';
export type DomainType = 'purchased' | 'connected';
export type DnsRecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT' | 'NS' | 'SRV' | 'CAA';

// Domain interfaces based on the OpenAPI spec
export interface Domain {
  id: number;
  name: string;
  createdAt: string;
  expiresAt: string;
}

export interface DomainSuggestion {
  name: string;
  price?: string;
  available: boolean;
  registration_period?: number;
}

export interface DomainContactInfo {
  first_name: string;
  last_name: string;
  email: string;
  organization_name?: string;
  job_title?: string;
  phone: string;
  fax?: string;
  address1: string;
  address2?: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

export interface ListDomain {
  id: number;
  name: string;
  status: DomainStatus;
  managedBy: ManagedBy;
  createdAt: Date;
  expiresAt?: Date;
  uuid: string;
  dnsManaged: boolean;
  nameserverVerifiedAt: string;
  siteId?: string;
  emailEnabled?: boolean;
  emailDomain?: string;
}

export interface DomainDetails {
  id: number;
  name: string;
  status: DomainStatus;
  type: DomainType;
  createdAt: Date;
  expiresAt?: Date;
  autoRenew: boolean;
  dnsRecords: InternalDnsRecord[];
  dnsManaged: boolean;
  nameserverVerifiedAt: string;
  siteId?: string;
}

export interface InternalDomain {
  id: number;
  uuid: string;
  uid: number;
  domain: string;
  registrar: string;
  registrar_domain_id: string;
  status: DomainStatus;
  registered_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  auto_renew?: boolean;
  dns_managed?: boolean;
  email_enabled?: boolean;
  nameservers_verified_at?: string;
  site_id?: string;
}

export interface DnsRecord {
  id: number;
  name: string;
  type: DnsRecordType;
  content: string;
  ttl: number;
  priority?: number;
}

export interface InternalDnsRecord {
  id: number;
  uuid: string;
  uid: number;
  domain_id: number;
  name: string;
  type: DnsRecordType;
  content: string;
  ttl: number;
  priority?: number;
  registrar_record_id: string;
  created_at: string;
  updated_at: string;
  record_locked?: boolean;
}

export interface RegisterDomainRequest {
  domain_name: string;
  auto_renew?: boolean;
  contact_info: DomainContactInfo;
  site_id?: string;
}

export interface AddDomainRequest {
  domain_name: string;
  dns_managed?: boolean;
  site_id?: string;
}

export interface CreateDnsRecordRequest {
  domain_name: string;
  name: string;
  type: DnsRecordType;
  content: string;
  ttl: number;
  priority?: number;
}

export interface UpdateDnsRecordRequest {
  uuid: string;
  name: string;
  type: DnsRecordType;
  content: string;
  ttl: number;
  priority?: number;
}

export interface DeleteDnsRecordRequest {
  uuid: string;
}

export interface DomainResponse {
  data: {
    domain: InternalDomain;
  };
}

export interface DnsRecordsResponse {
  data: {
    api_records: DnsRecord[];
    db_records: InternalDnsRecord[];
  };
}

export interface DnsRecordResponse {
  data: InternalDnsRecord;
}

export interface DeleteDnsRecordResponse {
  data: {
    success: boolean;
  };
}

export interface DomainPricing {
  domain: string;
  premium: boolean;
  registration_price: number;
  renewal_price: number;
  transfer_price: number;
  registration_price_formatted: string;
  renewal_price_formatted: string;
  transfer_price_formatted: string;
}

export interface DomainAutoRenew {
  domain: string;
  auto_renew: boolean;
  success: boolean;
  message?: string;
}

export interface DeleteDomainResponse {
  data: {
    success: boolean;
    message?: string;
  };
}

export interface VerifyNameserversResponse {
  data: {
    uuid: string;
    domain: string;
    nameservers_verified_at: string;
    verified: boolean;
  };
}

export interface DomainLinkResponse {
  data: {
    success: boolean;
    message?: string;
  };
}

export interface ConflictingDnsRecord {
  type: DnsRecordType;
  name: string;
  value: string;
}

export interface VerifyDnsResponse {
  success: boolean;
  message?: string;
  conflictingRecords?: ConflictingDnsRecord[];
}

// Represents the structure of the 'data' object in the API response
export interface DomainAvailability {
  domain: string;
  available: boolean;
  pricing?: {
    registration_price?: number | null;
    renewal_price?: number | null;
    transfer_price?: number | null;
    restore_price?: number | null;
    registration_price_formatted?: string;
    renewal_price_formatted?: string;
    transfer_price_formatted?: string;
    restore_price_formatted?: string;
  };
  registration_period?: number;
}

@Injectable({
  providedIn: 'root',
})
export class DomainsService {
  private apiUrl = `${environment.apiUrl}/domains`;

  constructor(private http: HttpClient) {}

  /**
   * List all domains for the authenticated user
   */
  listDomains(status?: 'active' | 'pending' | 'expired' | 'failed', search?: string): Observable<ListDomain[]> {
    const params: Record<string, string> = {};

    if (status) {
      params.status = status;
    }

    if (search) {
      params.search = search;
    }

    return this.http.get<{ data: { domains: InternalDomain[] } }>(`${this.apiUrl}`, { params }).pipe(
      map((response) => {
        if (response.data && Array.isArray(response.data.domains)) {
          return response.data.domains.map(
            (domain) =>
              ({
                id: domain.id,
                name: domain.domain,
                status: domain.status as 'active' | 'pending' | 'expired' | 'failed',
                managedBy: domain.dns_managed ? 'Hospitable' : ('External' as 'Hospitable' | 'External'),
                createdAt: new Date(domain.created_at),
                expiresAt: domain.expires_at ? new Date(domain.expires_at) : undefined,
                uuid: domain.uuid,
                dnsManaged: domain.dns_managed,
                nameserverVerifiedAt: domain.nameservers_verified_at,
                siteId: domain.site_id,
                emailEnabled: domain.email_enabled,
              }) as ListDomain
          );
        } else {
          throw new Error('Received unexpected data format from the server.');
        }
      })
    );
  }

  /**
   * Get a domain by its ID
   */
  getDomainById(id: number): Observable<{ data: { domain: InternalDomain } }> {
    return this.http.get<{ data: { domain: InternalDomain } }>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get a domain by its UUID
   */
  getDomainByUuid(uuid: string): Observable<DomainDetails> {
    // Use the same endpoint structure as getDomainById but with a uuid parameter
    return this.http.get<{ data: { domain: InternalDomain } }>(`${this.apiUrl}/${uuid}`).pipe(
      map((response) => {
        if (response && response.data && response.data.domain) {
          const domainData = response.data.domain;

          return {
            id: domainData.id,
            name: domainData.domain,
            status: domainData.status,
            type: domainData.registrar ? 'purchased' : 'connected',
            createdAt: new Date(domainData.created_at),
            expiresAt: domainData.expires_at ? new Date(domainData.expires_at) : undefined,
            autoRenew: domainData.auto_renew || false,
            dnsRecords: [],
            dnsManaged: domainData.dns_managed || false,
            nameserverVerifiedAt: domainData.nameservers_verified_at || '',
            siteId: domainData.site_id,
          };
        } else {
          throw new Error('Domain not found or could not be loaded.');
        }
      })
    );
  }

  /**
   * Register a new domain with the registrar
   */
  registerDomain(request: RegisterDomainRequest): Observable<DomainResponse> {
    return this.http.post<DomainResponse>(`${this.apiUrl}/register`, request);
  }

  /**
   * Add an existing domain to the system, optionally linking it to a site.
   */
  addExternalDomain(request: AddDomainRequest): Observable<DomainResponse> {
    return this.http.post<DomainResponse>(`${this.apiUrl}/external`, request);
  }

  /**
   * Get DNS records for a domain
   */
  getDnsRecords(domainName: string, source: 'api' | 'db' | 'both' = 'db'): Observable<DnsRecordsResponse> {
    return this.http.get<DnsRecordsResponse>(`${this.apiUrl}/dns-records`, {
      params: {
        domain_name: domainName,
        source,
      },
    });
  }

  /**
   * Create a new DNS record for a domain
   */
  createDnsRecord(request: CreateDnsRecordRequest): Observable<DnsRecordResponse> {
    return this.http.post<DnsRecordResponse>(`${this.apiUrl}/dns-records`, request);
  }

  /**
   * Update an existing DNS record for a domain
   */
  updateDnsRecord(request: UpdateDnsRecordRequest): Observable<DnsRecordResponse> {
    const { uuid, ...recordData } = request;
    return this.http.put<DnsRecordResponse>(`${this.apiUrl}/dns-records/${uuid}`, recordData);
  }

  /**
   * Delete an existing DNS record for a domain
   */
  deleteDnsRecord(request: DeleteDnsRecordRequest): Observable<DeleteDnsRecordResponse> {
    return this.http.delete<DeleteDnsRecordResponse>(`${this.apiUrl}/dns-records/${request.uuid}`);
  }

  /**
   * Check if a domain is available for registration
   * This method now returns more detailed information including pricing.
   */
  checkDomainAvailability(domainName: string): Observable<DomainAvailability[]> {
    // Return the detailed data object
    return this.http
      .post<{ data: DomainAvailability[] }>(`${this.apiUrl}/check-availability`, {
        // Expect the new API response structure
        domain_name: domainName,
      })
      .pipe(
        map((response) =>
          response.data.map((domain) => ({
            ...domain,
            registration_period: domain.registration_period || 1, // Default to 1 if not provided
          }))
        )
      );
  }

  /**
   * Get pricing information for a domain
   */
  getDomainPricing(domainName: string): Observable<DomainPricing> {
    return this.http
      .post<{ data: DomainPricing }>(`${this.apiUrl}/pricing`, {
        domain_name: domainName,
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Toggle auto-renew setting for a domain
   */
  toggleDomainAutoRenew(domainName: string, autoRenew: boolean): Observable<DomainAutoRenew> {
    return this.http
      .post<{ data: DomainAutoRenew }>(`${this.apiUrl}/toggle-auto-renew`, {
        domain_name: domainName,
        auto_renew: autoRenew,
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Delete a domain from the system
   */
  deleteDomain(uuid: string): Observable<DeleteDomainResponse> {
    return this.http.delete<DeleteDomainResponse>(`${this.apiUrl}/${uuid}`);
  }

  /**
   * Verify DNS records (A/CNAME for external, NS for managed) for a domain
   */
  verifyDomainDnsRecords(uuid: string): Observable<VerifyDnsResponse> {
    return this.http.post<VerifyDnsResponse>(`${this.apiUrl}/${uuid}/verify-dns`, {});
  }

  /**
   * Unlink a domain from a direct site
   */
  unlinkDomainFromSite(domainUuid: string): Observable<DomainLinkResponse> {
    // Assuming the site_id is needed in the body or query params, adjust as necessary
    return this.http.delete<DomainLinkResponse>(`${this.apiUrl}/${domainUuid}/link-to-site`);
  }

  getContactInfo(): Observable<DomainContactInfo> {
    return this.http.get<{ data: DomainContactInfo }>(`${this.apiUrl}/contact`).pipe(map((response) => response.data));
  }

  // It's crucial to also have an updateContactInfo method that does the reverse mapping
  // if you are sending updates back to the API.
  // Example (you'll need to adjust endpoint and request body structure):
  /*
  updateContactInfo(contactInfo: DomainContactInfo): Observable<any> {
    const apiRequestBody = {
      first_name: contactInfo.firstName,
      last_name: contactInfo.lastName,
      email: contactInfo.email,
      organization_name: contactInfo.organizationName,
      job_title: contactInfo.jobTitle,
      phone: contactInfo.phone,
      fax: contactInfo.fax,
      address1: contactInfo.address,
      address2: contactInfo.aptOrSuite,
      city: contactInfo.city,
      state_province: contactInfo.stateOrProvince,
      postal_code: contactInfo.zipPostalCode,
      country: contactInfo.country, // This would need to be alpha2code if API expects that on update
    };
    return this.http.put(`${this.apiUrl}/contact`, { data: apiRequestBody });
  }
  */
}
